/**
 * Configuración del Sistema de Bloques Horarios de Exocortex
 *
 * Los bloques horarios organizan las tareas diarias en períodos naturales
 * del día, facilitando la planificación y reduciendo la carga cognitiva
 * de decidir "cuándo" hacer cada cosa.
 *
 * Los horarios se calculan dinámicamente basados en wakeTime y sleepTime del usuario.
 */

import type { TimeBlock, TimeBlockGroup, Protocol } from '@/core/types';

/**
 * Configuración de cada bloque horario
 */
export interface TimeBlockConfig {
  id: TimeBlock;
  label: string;
  description: string;
  timeRange: string;
  startHour: number;
  endHour: number;
  color: {
    bg: string;
    text: string;
    border: string;
    accent: string;
    bgTransparent: string;
  };
  icon: string;
}

/**
 * Colores base para cada bloque (no cambian con horarios)
 */
const TIME_BLOCK_COLORS: Record<TimeBlock, TimeBlockConfig['color']> = {
  morning: {
    bg: 'bg-sky-500',
    text: 'text-sky-400',
    border: 'border-sky-700',
    accent: '#0ea5e9',
    bgTransparent: 'bg-sky-900/30',
  },
  afternoon: {
    bg: 'bg-orange-500',
    text: 'text-orange-400',
    border: 'border-orange-700',
    accent: '#f97316',
    bgTransparent: 'bg-orange-900/30',
  },
  evening: {
    bg: 'bg-violet-500',
    text: 'text-violet-400',
    border: 'border-violet-700',
    accent: '#8b5cf6',
    bgTransparent: 'bg-violet-900/30',
  },
  anytime: {
    bg: 'bg-slate-500',
    text: 'text-slate-400',
    border: 'border-slate-600',
    accent: '#64748b',
    bgTransparent: 'bg-slate-800/30',
  },
};

/**
 * Formatear hora decimal a string HH:mm
 */
function formatHour(hour: number): string {
  const h = Math.floor(hour);
  const m = Math.round((hour - h) * 60);
  return `${h}:${m.toString().padStart(2, '0')}`;
}

/**
 * Calcular los bloques horarios basados en wakeTime y sleepTime
 */
export function calculateTimeBlocks(wakeTime: string, sleepTime: string): Record<TimeBlock, TimeBlockConfig> {
  const wake = parseIdealTime(wakeTime);
  const sleep = parseIdealTime(sleepTime);

  // Calcular el día activo (desde despertar hasta dormir)
  const activeHours = sleep > wake ? sleep - wake : (24 - wake) + sleep;

  // Dividir el día en 3 bloques iguales
  const blockDuration = activeHours / 3;

  const morningEnd = wake + blockDuration;
  const afternoonEnd = morningEnd + blockDuration;

  return {
    morning: {
      id: 'morning',
      label: 'Mañana',
      description: 'Despertar, desayuno, inicio del día',
      timeRange: `${formatHour(wake)} - ${formatHour(morningEnd)}`,
      startHour: wake,
      endHour: morningEnd,
      color: TIME_BLOCK_COLORS.morning,
      icon: 'Sunrise',
    },
    afternoon: {
      id: 'afternoon',
      label: 'Tarde',
      description: 'Trabajo, almuerzo, actividades principales',
      timeRange: `${formatHour(morningEnd)} - ${formatHour(afternoonEnd)}`,
      startHour: morningEnd,
      endHour: afternoonEnd,
      color: TIME_BLOCK_COLORS.afternoon,
      icon: 'Sun',
    },
    evening: {
      id: 'evening',
      label: 'Noche',
      description: 'Post-trabajo, cena, ritual de sueño',
      timeRange: `${formatHour(afternoonEnd)} - ${formatHour(sleep)}`,
      startHour: afternoonEnd,
      endHour: sleep,
      color: TIME_BLOCK_COLORS.evening,
      icon: 'Moon',
    },
    anytime: {
      id: 'anytime',
      label: 'Flexible',
      description: 'Sin horario fijo, puede hacerse en cualquier momento',
      timeRange: 'Cualquier momento',
      startHour: wake,
      endHour: sleep,
      color: TIME_BLOCK_COLORS.anytime,
      icon: 'Clock',
    },
  };
}

/**
 * Bloques por defecto (wakeTime: 07:00, sleepTime: 23:00)
 */
export const TIME_BLOCKS: Record<TimeBlock, TimeBlockConfig> = calculateTimeBlocks('07:00', '23:00');

/**
 * Orden de los bloques horarios para visualización
 */
export const TIME_BLOCK_ORDER: TimeBlock[] = [
  'morning',
  'afternoon',
  'evening',
  'anytime',
];

/**
 * Obtener configuración de un bloque horario
 */
export function getTimeBlockConfig(
  timeBlock: TimeBlock,
  blocks: Record<TimeBlock, TimeBlockConfig> = TIME_BLOCKS
): TimeBlockConfig {
  return blocks[timeBlock];
}

/**
 * Obtener el label de un bloque horario
 */
export function getTimeBlockLabel(timeBlock: TimeBlock): string {
  return TIME_BLOCKS[timeBlock].label;
}

/**
 * Obtener los colores de un bloque horario
 */
export function getTimeBlockColors(timeBlock: TimeBlock) {
  return TIME_BLOCK_COLORS[timeBlock];
}

/**
 * Determinar en qué bloque horario cae una hora específica
 */
export function getTimeBlockFromHour(
  hour: number,
  blocks: Record<TimeBlock, TimeBlockConfig> = TIME_BLOCKS
): TimeBlock {
  if (hour >= blocks.morning.startHour && hour < blocks.morning.endHour) return 'morning';
  if (hour >= blocks.afternoon.startHour && hour < blocks.afternoon.endHour) return 'afternoon';
  if (hour >= blocks.evening.startHour && hour < blocks.evening.endHour) return 'evening';
  return 'anytime';
}

/**
 * Determinar en qué bloque horario estamos actualmente
 */
export function getCurrentTimeBlock(
  blocks: Record<TimeBlock, TimeBlockConfig> = TIME_BLOCKS
): TimeBlock {
  const now = new Date();
  const hour = now.getHours() + now.getMinutes() / 60;
  return getTimeBlockFromHour(hour, blocks);
}

/**
 * Parsear hora ideal (HH:mm) a número decimal
 */
export function parseIdealTime(idealTime: string): number {
  const parts = idealTime.split(':').map(Number);
  const hours = parts[0] ?? 0;
  const minutes = parts[1] ?? 0;
  return hours + minutes / 60;
}

/**
 * Ordenar protocolos por hora ideal dentro de un bloque
 */
export function sortByIdealTime(protocols: Protocol[]): Protocol[] {
  return [...protocols].sort((a, b) => {
    const timeA = a.idealTime ? parseIdealTime(a.idealTime) : 99;
    const timeB = b.idealTime ? parseIdealTime(b.idealTime) : 99;
    return timeA - timeB;
  });
}

/**
 * Agrupar protocolos por bloque horario
 */
export function groupProtocolsByTimeBlock(
  protocols: Protocol[],
  blocks: Record<TimeBlock, TimeBlockConfig> = TIME_BLOCKS
): TimeBlockGroup[] {
  // Solo protocolos diarios
  const dailyProtocols = protocols.filter((p) => p.frequency === 'daily');

  // Crear grupos vacíos para cada bloque
  const groups: Record<TimeBlock, Protocol[]> = {
    morning: [],
    afternoon: [],
    evening: [],
    anytime: [],
  };

  // Distribuir protocolos en sus bloques
  for (const protocol of dailyProtocols) {
    const block = protocol.timeBlock || 'anytime';
    groups[block].push(protocol);
  }

  // Convertir a array de TimeBlockGroup ordenado
  return TIME_BLOCK_ORDER.map((block) => {
    const blockProtocols = sortByIdealTime(groups[block]);
    const config = blocks[block];

    return {
      timeBlock: block,
      label: config.label,
      timeRange: config.timeRange,
      protocols: blockProtocols,
      totalTime: blockProtocols.reduce((sum, p) => sum + p.timeEstimate, 0),
      completedCount: blockProtocols.filter((p) => p.isCompleted).length,
      totalCount: blockProtocols.length,
      isFullyCompleted: blockProtocols.every((p) => p.isCompleted),
    };
  }).filter((group) => group.totalCount > 0); // Solo bloques con protocolos
}

/**
 * Obtener el bloque horario activo (el actual o el siguiente con tareas pendientes)
 */
export function getActiveTimeBlock(
  protocols: Protocol[],
  blocks: Record<TimeBlock, TimeBlockConfig> = TIME_BLOCKS
): TimeBlock {
  const currentBlock = getCurrentTimeBlock(blocks);
  const groups = groupProtocolsByTimeBlock(protocols, blocks);

  // Buscar el primer bloque no completado empezando por el actual
  const currentIndex = TIME_BLOCK_ORDER.indexOf(currentBlock);

  for (let i = currentIndex; i < TIME_BLOCK_ORDER.length; i++) {
    const block = TIME_BLOCK_ORDER[i] as TimeBlock;
    const group = groups.find((g) => g.timeBlock === block);
    if (group && !group.isFullyCompleted) {
      return block;
    }
  }

  // Si todos los bloques posteriores están completos, buscar anteriores
  for (let i = 0; i < currentIndex; i++) {
    const block = TIME_BLOCK_ORDER[i] as TimeBlock;
    const group = groups.find((g) => g.timeBlock === block);
    if (group && !group.isFullyCompleted) {
      return block;
    }
  }

  return currentBlock;
}
